import { createContext, useContext, useEffect, useState, useCallback } from 'react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;
const CopyContext = createContext({ overrides: {}, ready: false, refresh: () => {} });

/**
 * MarketingCopyProvider — fetches `/api/marketing-copy` once on mount, caches
 * overrides in context. Pages call `useCopy(key, fallback)` which returns the
 * override if present, else the JSX fallback.
 *
 * Why session-scoped vs query-string-based?
 *  - Editors using the Super Admin tab live-refresh the overrides cache
 *    via a manual refresh button; consumer page-views read once per
 *    session, which is the right trade-off for marketing copy.
 */
export function MarketingCopyProvider({ children }) {
  const [overrides, setOverrides] = useState({});
  const [ready, setReady] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/api/marketing-copy`);
      setOverrides(res.data?.overrides || {});
    } catch {
      // Page render must continue with defaults even if the endpoint blips.
      setOverrides({});
    }
    setReady(true);
  }, []);

  useEffect(() => { load(); }, [load]);

  return (
    <CopyContext.Provider value={{ overrides, ready, refresh: load }}>
      {children}
    </CopyContext.Provider>
  );
}

/**
 * useCopy(key, fallback?) — returns the active copy for a slot.
 *   - If an override exists in the cache, that wins.
 *   - Otherwise returns the `fallback` you pass in JSX (which is the
 *     canonical default baked into the codebase).
 */
export function useCopy(key, fallback = '') {
  const { overrides } = useContext(CopyContext);
  if (overrides && Object.prototype.hasOwnProperty.call(overrides, key)) {
    return overrides[key];
  }
  return fallback;
}

export function useCopyRefresh() {
  return useContext(CopyContext).refresh;
}
